/**
 * Integrytas Group - Base App (MOBILE)
 * Page: PDE.DASHBOARD — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/PdeDashboardPage.tsx. A distinct
 * presentation (headline KPI tiles + grouped metric lists) over the SAME data
 * layer: it reads the live "now" view from the admin-gated pde_snapshot() RPC
 * through apiDb (RLS/admin enforced server-side by the gateway JWT). READ-ONLY —
 * a diagnostics dashboard has no writes, so it never touches the Common Routine.
 * §16: this is a purpose-built mobile screen, never the desktop grid re-hosted.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, Pause, Play, Inbox, Activity } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useFormatDateTime } from '../../lib/format_date';
import type { MobilePageComponentProps } from '../mobile_page_components';

// Mirrors the shape returned by pde_snapshot() (see the desktop page).
interface Snapshot {
  captured_at: string;
  db: {
    active_connections: number;
    total_connections: number;
    max_connections: number;
    connection_pct: number | null;
    cache_hit_ratio: number | null;
    commits: number;
    rollbacks: number;
    deadlocks: number;
    db_size_bytes: number;
    longest_query_sec: number;
    temp_bytes: number;
  };
  app: { errors_5m: number; errors_1h: number; fatal_5m: number; audit_writes_5m: number };
  tasks: { pending: number; running: number; failed: number };
  web: { page_load_avg_ms: number | null; ttfb_avg_ms: number | null; samples_5m: number };
  error_code?: string;
}

const INTERVALS = [5, 10, 30, 60] as const;
const DEFAULT_INTERVAL_SEC = 10;
const HIGH_CONNECTION_PCT = 80;
const MIN_CACHE_HIT_RATIO = 0.9;
const LONG_QUERY_SEC = 30;

const DASH = '—';

function bytes(n: number | null | undefined): string {
  if (n == null) return DASH;
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

function pct(r: number | null | undefined): string {
  return r == null ? DASH : `${(r * 100).toFixed(1)}%`;
}

function ms(n: number | null | undefined): string {
  return n == null ? DASH : `${n} ms`;
}

// ---------------------------------------------------------------------------
// Presentational helpers
// ---------------------------------------------------------------------------
function KpiTile({ label, value, alert }: { label: string; value: string; alert?: boolean }) {
  return (
    <div className={'m-kpi' + (alert ? ' m-kpi-alert' : '')}>
      <div className="m-kpi-l">{label}</div>
      <div className="m-kpi-v">{value}</div>
    </div>
  );
}

function MetricRow({ label, value, danger }: { label: string; value: ReactNode; danger?: boolean }) {
  return (
    <div className="m-field">
      <span className="m-k">{label}</span>
      <span className="m-v">
        {danger ? <span className="m-pill m-p-no">{value}</span> : value}
      </span>
    </div>
  );
}

export function MobilePdeDashboardPage(_props: MobilePageComponentProps) {
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const [snap, setSnap] = useState<Snapshot | null>(null);
  const [intervalSec, setIntervalSec] = useState<number>(DEFAULT_INTERVAL_SEC);
  const [paused, setPaused] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const capturedAt = useRef<string | null>(null);

  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      const { data, error: e } = await apiDb.rpc('pde_snapshot');
      if (e) throw new Error(e.message);
      const s = data as Snapshot | null;
      if (s?.error_code) throw new Error(s.error_code);
      capturedAt.current = s?.captured_at ?? null;
      setSnap(s);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }, []);

  // Initial load.
  useEffect(() => { void refresh(); }, [refresh]);

  // Live polling on the selected interval, unless paused. `refresh` is a stable
  // useCallback([]) so it never re-arms the timer on its own.
  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => { void refresh(); }, intervalSec * 1000);
    return () => clearInterval(id);
  }, [paused, intervalSec, refresh]);

  const db = snap?.db;
  const app = snap?.app;
  const tasks = snap?.tasks;
  const web = snap?.web;

  const updatedLabel = snap
    ? paused
      ? t('mobile.pde.paused', 'Paused')
      : t('mobile.pde.updated', { at: formatDateTime(capturedAt.current), defaultValue: `Updated ${formatDateTime(capturedAt.current)}` })
    : t('mobile.pde.live', 'Live diagnostics');

  return (
    <section className="m-screen">
      <div className="m-greet">
        <div className="m-hi">{t('mobile.pde.title', 'System Diagnostics')}</div>
        <div className="m-sub">{updatedLabel}</div>
      </div>

      {/* Controls: refresh / pause + polling interval */}
      <div className="m-chips">
        <button type="button" className="m-chip" disabled={busy} onClick={() => void refresh()}>
          <RefreshCw size={13} /> {t('mobile.pde.refresh', 'Refresh')}
        </button>
        <button
          type="button"
          className={'m-chip' + (paused ? ' m-active' : '')}
          onClick={() => setPaused((p) => !p)}
        >
          {paused ? <Play size={13} /> : <Pause size={13} />}
          {paused ? t('mobile.pde.resume', 'Resume') : t('mobile.pde.pause', 'Pause')}
        </button>
      </div>
      <div className="m-chips">
        {INTERVALS.map((s) => (
          <button
            key={s}
            type="button"
            className={'m-chip' + (intervalSec === s ? ' m-active' : '')}
            onClick={() => setIntervalSec(s)}
          >
            {t('mobile.pde.every_seconds', { s, defaultValue: `${s}s` })}
          </button>
        ))}
      </div>

      {error && (
        <div className="m-card">
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </div>
      )}

      {!snap && !error && (
        <div className="m-card">
          <div className="m-empty"><Activity /> {t('mobile.pde.loading', 'Loading diagnostics…')}</div>
        </div>
      )}

      {/* Headline KPI tiles */}
      {db && app && tasks && (
        <div className="m-kpis">
          <KpiTile
            label={t('mobile.pde.kpi.connections', 'Connections')}
            value={`${db.total_connections}/${db.max_connections}`}
            alert={(db.connection_pct ?? 0) > HIGH_CONNECTION_PCT}
          />
          <KpiTile
            label={t('mobile.pde.kpi.cache_hit', 'Cache Hit')}
            value={pct(db.cache_hit_ratio)}
            alert={(db.cache_hit_ratio ?? 1) < MIN_CACHE_HIT_RATIO}
          />
          <KpiTile
            label={t('mobile.pde.kpi.errors_5m', 'Errors (5m)')}
            value={String(app.errors_5m)}
            alert={app.errors_5m > 0}
          />
          <KpiTile
            label={t('mobile.pde.kpi.tasks_failed', 'Tasks Failed')}
            value={String(tasks.failed)}
            alert={tasks.failed > 0}
          />
        </div>
      )}

      {/* Database */}
      {db && (
        <>
          <div className="m-sect"><h2>{t('mobile.pde.section.database', 'Database')}</h2></div>
          <div className="m-card"><div className="m-fieldcard">
            <MetricRow label={t('mobile.pde.metric.active_connections', 'Active connections')} value={`${db.active_connections} / ${db.max_connections}`} />
            <MetricRow label={t('mobile.pde.metric.connection_load', 'Connection load')} value={`${db.connection_pct ?? 0}%`} danger={(db.connection_pct ?? 0) > HIGH_CONNECTION_PCT} />
            <MetricRow label={t('mobile.pde.metric.cache_hit_ratio', 'Cache hit ratio')} value={pct(db.cache_hit_ratio)} danger={(db.cache_hit_ratio ?? 1) < MIN_CACHE_HIT_RATIO} />
            <MetricRow label={t('mobile.pde.metric.database_size', 'Database size')} value={bytes(db.db_size_bytes)} />
            <MetricRow label={t('mobile.pde.metric.longest_query', 'Longest query')} value={`${db.longest_query_sec}s`} danger={db.longest_query_sec > LONG_QUERY_SEC} />
            <MetricRow label={t('mobile.pde.metric.deadlocks', 'Deadlocks')} value={db.deadlocks} danger={db.deadlocks > 0} />
            <MetricRow label={t('mobile.pde.metric.commits', 'Commits')} value={db.commits.toLocaleString()} />
            <MetricRow label={t('mobile.pde.metric.rollbacks', 'Rollbacks')} value={db.rollbacks.toLocaleString()} danger={db.rollbacks > db.commits} />
            <MetricRow label={t('mobile.pde.metric.temp_bytes', 'Temp bytes')} value={bytes(db.temp_bytes)} />
          </div></div>
        </>
      )}

      {/* Application health */}
      {app && tasks && (
        <>
          <div className="m-sect"><h2>{t('mobile.pde.section.application_health', 'Application Health')}</h2></div>
          <div className="m-card"><div className="m-fieldcard">
            <MetricRow label={t('mobile.pde.metric.errors_5m', 'Errors (5m)')} value={app.errors_5m} danger={app.errors_5m > 0} />
            <MetricRow label={t('mobile.pde.metric.errors_1h', 'Errors (1h)')} value={app.errors_1h.toLocaleString()} />
            <MetricRow label={t('mobile.pde.metric.fatal_5m', 'Fatal (5m)')} value={app.fatal_5m} danger={app.fatal_5m > 0} />
            <MetricRow label={t('mobile.pde.metric.audit_writes_5m', 'Audit writes (5m)')} value={app.audit_writes_5m.toLocaleString()} />
            <MetricRow label={t('mobile.pde.metric.tasks_pending', 'Tasks pending')} value={tasks.pending.toLocaleString()} />
            <MetricRow label={t('mobile.pde.metric.tasks_running', 'Tasks running')} value={tasks.running.toLocaleString()} />
            <MetricRow label={t('mobile.pde.metric.tasks_failed', 'Tasks failed')} value={tasks.failed} danger={tasks.failed > 0} />
          </div></div>
        </>
      )}

      {/* Client performance */}
      {web && (
        <>
          <div className="m-sect"><h2>{t('mobile.pde.section.client_performance', 'Client Performance (last 5m)')}</h2></div>
          <div className="m-card"><div className="m-fieldcard">
            <MetricRow label={t('mobile.pde.metric.avg_page_load', 'Avg page load')} value={ms(web.page_load_avg_ms)} />
            <MetricRow label={t('mobile.pde.metric.avg_ttfb', 'Avg TTFB')} value={ms(web.ttfb_avg_ms)} />
            <MetricRow label={t('mobile.pde.metric.vital_samples', 'Vital samples')} value={web.samples_5m.toLocaleString()} />
          </div></div>
        </>
      )}

      {snap && !db && !error && (
        <div className="m-card"><div className="m-empty"><Inbox /> {t('mobile.pde.no_data', 'No diagnostics available')}</div></div>
      )}
    </section>
  );
}
